/**
 * FoundationApprovalQueuePanel — manager-facing surface for the Foundation
 * Phase-2 push-log approval gate (iter155) + the SLA alert from iter156.
 *
 * Renders three groups:
 *  1. **SLA breach banner** — red bar if any row > FOUNDATION_APPROVAL_SLA_HOURS
 *     (default 24 h) showing the oldest stale row's age + count. Polled
 *     every 60 s from `GET /api/integrations/foundation/sla-alerts`.
 *  2. **Pending approval queue** — rows in `status=pending_approval` with
 *     per-row **Approve** / **Reject** actions. Approve is one-click;
 *     Reject opens an inline reason prompt before firing.
 *  3. **Recent activity** — last 10 rows in any status so managers can
 *     audit who approved/rejected what.
 *
 * RBAC: the endpoints are admin-only via `ADMIN_WRITE_PREFIXES`. This
 * component renders regardless; on 403 it shows a "not authorized"
 * empty state instead of trying to surface admin actions.
 *
 * Refresh strategy: REST polling every 30 s. Cheap; nothing here demands
 * SSE because approvals are low-frequency (a few per day at most).
 */
import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertTriangle, CheckCircle2, XCircle, Clock, ChevronDown,
  FileText, Loader2, ShieldAlert, History,
} from "lucide-react";
import { toast } from "sonner";
import FoundationWoQueue from "./FoundationWoQueue";
import FoundationBudgetRegister from "./FoundationBudgetRegister";
import FoundationARQueue from "./FoundationARQueue";
import { ApprovalQueueSection } from "./ApprovalQueueSection";
import { PanelHeader, PanelCount, CollapsibleSection } from "@/components/settings/SettingsUI";
import {
  getFoundationPushLog,
  approveFoundationPushLog,
  rejectFoundationPushLog,
  getFoundationSlaAlerts,
} from "@/lib/api";

const POLL_INTERVAL_MS = 30_000;

const STATUS_TINT = {
  pending_approval: { bg: "bg-amber-50",  text: "text-amber-800",  badge: "bg-amber-100 border-amber-300", label: "Pending Approval" },
  pending_bridge:   { bg: "bg-blue-50",   text: "text-blue-800",   badge: "bg-blue-100 border-blue-300",   label: "Approved → Bridge" },
  rejected:         { bg: "bg-rose-50",   text: "text-rose-800",   badge: "bg-rose-100 border-rose-300",   label: "Rejected" },
  sent:             { bg: "bg-emerald-50", text: "text-emerald-800", badge: "bg-emerald-100 border-emerald-300", label: "Sent" },
  failed:           { bg: "bg-slate-100", text: "text-slate-700",  badge: "bg-slate-200 border-slate-300", label: "Failed" },
};

const fmtAgeHours = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

const fmtTimestamp = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
};

function PendingRow({ row, onApprove, onReject, busy }) {
  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [reason, setReason] = useState("");
  const ageH = row._age_hours; // optionally injected from SLA snapshot
  const isStale = ageH != null && ageH > 24;

  return (
    <div
      className={`border rounded-lg p-3 ${isStale ? "border-amber-300 bg-amber-50/50" : "border-slate-200 bg-white"}`}
      data-testid={`foundation-pending-row-${row.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              {row.doc_type?.replace("_", " ") || "—"}
            </span>
            {/* iter206 (ticket #269) — surface the Foundation target
                table so admins can confirm the row is bound for the
                accounting side (jc_jobs) and NOT Service Dispatch. */}
            {row.foundation_target_table && (
              <span
                className="text-[10px] font-mono font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded"
                title={`Bridge will INSERT into Foundation table ${row.foundation_target_table}`}
                data-testid={`foundation-target-${row.id}`}
              >
                → {row.foundation_target_table}
              </span>
            )}
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${STATUS_TINT.pending_approval.badge} ${STATUS_TINT.pending_approval.text}`}>
              Pending
            </span>
            {isStale && (
              <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded border border-amber-300 inline-flex items-center gap-1" title="Exceeded 24 h SLA">
                <AlertTriangle className="w-3 h-3" />
                SLA {fmtAgeHours(ageH)}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500">
            <span>{row.operator || "—"}</span>
            <span className="mx-1.5">·</span>
            <Clock className="w-3 h-3 inline mr-0.5 -mt-0.5" />
            {fmtTimestamp(row.created_at)}
          </div>
          <details className="mt-2">
            <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-700">
              View payload ({Object.keys(row.payload || {}).length} fields)
            </summary>
            <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded p-2 mt-1 overflow-x-auto max-h-32" data-testid={`foundation-payload-${row.id}`}>
              {JSON.stringify(row.payload || {}, null, 2)}
            </pre>
          </details>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button
            size="sm"
            onClick={() => onApprove(row.id)}
            disabled={busy}
            className="h-7 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`foundation-approve-${row.id}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowRejectPrompt(s => !s)}
            disabled={busy}
            className="h-7 px-2.5 text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
            data-testid={`foundation-reject-btn-${row.id}`}
          >
            <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
          </Button>
        </div>
      </div>
      {showRejectPrompt && (
        <div className="mt-2 pt-2 border-t border-slate-200 space-y-1.5" data-testid={`foundation-reject-prompt-${row.id}`}>
          <label className="text-[11px] font-medium text-slate-600">
            Reason (optional, captured for compliance):
          </label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. duplicate of WO-1234"
            className="h-7 text-xs"
            data-testid={`foundation-reject-reason-${row.id}`}
          />
          <div className="flex gap-1.5 justify-end">
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => { setShowRejectPrompt(false); setReason(""); }}>Cancel</Button>
            <Button
              size="sm"
              onClick={() => { onReject(row.id, reason); setShowRejectPrompt(false); setReason(""); }}
              className="h-6 px-2 text-xs bg-rose-600 hover:bg-rose-700 text-white"
              data-testid={`foundation-confirm-reject-${row.id}`}
            >
              Confirm Reject
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActivityRow({ row }) {
  const tint = STATUS_TINT[row.status] || STATUS_TINT.failed;
  const audit = row.approved_at
    ? `Approved by ${row.approved_by || "—"} · ${fmtTimestamp(row.approved_at)}`
    : row.rejected_at
      ? `Rejected by ${row.rejected_by || "—"} · ${fmtTimestamp(row.rejected_at)}${row.rejected_reason ? ` · "${row.rejected_reason}"` : ""}`
      : null;
  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${tint.bg}`} data-testid={`foundation-activity-row-${row.id}`}>
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${tint.badge} ${tint.text}`}>{tint.label}</span>
      <span className="font-mono text-[11px] text-slate-600 truncate flex-1">
        {row.doc_type}
        {row.foundation_target_table && (
          <span
            className="ml-1.5 text-emerald-700 font-semibold"
            title={`→ Foundation ${row.foundation_target_table}`}
            data-testid={`foundation-activity-target-${row.id}`}
          >
            → {row.foundation_target_table}
          </span>
        )}
        <span className="text-slate-400 mx-1">·</span>
        {row.operator || "—"}
      </span>
      <span className="text-[10px] text-slate-500 shrink-0" title={fmtTimestamp(row.created_at)}>
        {fmtTimestamp(row.created_at).split(",")[0]}
      </span>
      {audit && (
        <span className="text-[10px] text-slate-500 truncate max-w-[280px]" title={audit}>· {audit}</span>
      )}
    </div>
  );
}

export default function FoundationApprovalQueuePanel() {
  const [pending, setPending] = useState([]);
  const [recent, setRecent] = useState([]);
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [woCount, setWoCount] = useState(0);
  const [arCount, setArCount] = useState(0);
  const [docCount, setDocCount] = useState(0);

  const reload = useCallback(async (opts = {}) => {
    try {
      // Three parallel requests: pending list, recent activity (any
      // status), and the SLA breach summary.
      const [pendingRes, recentRes, slaRes] = await Promise.all([
        getFoundationPushLog({ status: "pending_approval", limit: 50 }),
        getFoundationPushLog({ limit: 10 }),
        getFoundationSlaAlerts(),
      ]);
      setPending(pendingRes.data || []);
      setRecent(recentRes.data || []);
      setSla(slaRes.data || null);
      setForbidden(false);
      if (opts.manual) toast.success("Approval queue refreshed");
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else if (opts.manual) {
        toast.error(e?.response?.data?.detail || "Refresh failed — could not reach the server");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const t = setInterval(() => reload(), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [reload]);

  const handleApprove = async (id) => {
    setActingId(id);
    try {
      const res = await approveFoundationPushLog(id);
      const status = res.data?.status;
      if (status === "already_approved") {
        toast.info("Row was already approved by a teammate.");
      } else {
        toast.success("Approved. The bridge will pick it up on the next poll.");
      }
      // Optimistically drop from pending; reload syncs in 30 s anyway.
      setPending(prev => prev.filter(r => r.id !== id));
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to approve.";
      toast.error(detail);
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (id, reason) => {
    setActingId(id);
    try {
      const res = await rejectFoundationPushLog(id, reason);
      const status = res.data?.status;
      if (status === "already_rejected") {
        toast.info("Row was already rejected by a teammate.");
      } else {
        toast.success(reason ? `Rejected: ${reason.slice(0, 60)}` : "Rejected.");
      }
      setPending(prev => prev.filter(r => r.id !== id));
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to reject.";
      toast.error(detail);
    } finally {
      setActingId(null);
    }
  };

  // Inject SLA age_hours into pending rows so PendingRow can render the
  // SLA badge inline (instead of re-fetching the snapshot per row).
  const slaByRow = new Map();
  (sla?.stale_rows || []).forEach(r => slaByRow.set(r.id, r.age_hours));
  const pendingDecorated = pending.map(r => ({ ...r, _age_hours: slaByRow.get(r.id) }));

  const staleCount = sla?.stale_count || 0;

  return (
    <div data-testid="foundation-approval-section">
      <PanelHeader
        icon={ShieldAlert}
        iconClass="text-amber-600"
        title="Foundation Approval Queue"
        hint="Work Orders · AR · approvals · admin-only (POs and Change Orders are sent from the Sync tab)"
        testId="toggle-foundation-approval"
        actions={
          <>
            {woCount > 0 && (
              <PanelCount tone="accent" testId="foundation-wo-badge" value={`${woCount} WO${woCount === 1 ? "" : "s"} awaiting`} />
            )}
            {arCount > 0 && (
              <PanelCount tone="accent" testId="foundation-ar-badge" value={`${arCount} AR invoice${arCount === 1 ? "" : "s"} awaiting`} />
            )}
            {docCount > 0 && (
              <PanelCount tone="warn" testId="foundation-pending-badge" value={`${docCount} pending`} />
            )}
            {staleCount > 0 && (
              <PanelCount tone="danger" testId="foundation-sla-badge" value={`${staleCount} stale`} />
            )}
          </>
        }
      />

      {true && (
        <div className="px-5 py-4 space-y-4" data-testid="foundation-approval-body">
          {forbidden && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-500" data-testid="foundation-forbidden">
              You're not authorized to manage the Foundation approval queue. Admin role required.
            </div>
          )}

          {!forbidden && (
            <>
              {/* SLA breach banner */}
              {sla && sla.stale_count > 0 && (
                <div className="bg-rose-50 border border-rose-300 rounded-lg p-3 flex items-center gap-3" data-testid="foundation-sla-banner">
                  <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-rose-800">
                      {sla.stale_count} row{sla.stale_count === 1 ? "" : "s"} past the {sla.threshold_hours} h SLA
                    </div>
                    <div className="text-xs text-rose-700 mt-0.5">
                      Oldest waiting <strong>{fmtAgeHours(sla.oldest_age_hours)}</strong>. Approve or reject below to clear the breach.
                    </div>
                  </div>
                </div>
              )}

              {/* Work Orders awaiting Foundation (SP path) */}
              <FoundationWoQueue onCountChange={setWoCount} />

              {/* iter314 — pushed jobs → Genie budget import CSV (download only) */}
              <FoundationBudgetRegister />

              {/* AR Invoices awaiting Foundation (direct ar_invoice write) */}
              <FoundationARQueue onCountChange={setArCount} />

              {/* SVC-FIX-2 — unified queue: POs · COs · Foundation push rows */}
              <ApprovalQueueSection
                pushRows={pendingDecorated}
                loadingPush={loading}
                onCountChange={setDocCount}
                renderPushRow={(row) => (
                  <PendingRow key={row.id} row={row} onApprove={handleApprove} onReject={handleReject} busy={actingId === row.id} />
                )}
              />

              {/* Recent activity */}
              <CollapsibleSection
                id="foundation-activity" testId="toggle-foundation-activity"
                icon={History} iconClass="text-slate-400"
                title={`Recent activity (${recent.length})`}
                defaultOpen={false}
              >
                <div className="space-y-1" data-testid="foundation-recent-activity">
                  {recent.length === 0 ? (
                    <div className="text-xs text-slate-400 text-center py-3">No activity yet.</div>
                  ) : (
                    recent.map(row => <ActivityRow key={row.id} row={row} />)
                  )}
                </div>
              </CollapsibleSection>
            </>
          )}
        </div>
      )}
    </div>
  );
}
